import { SxProps } from '@mui/material'
import { scaledPx } from '@/styles'
import { sidebarGutter } from '../SidebarLayout.style'

interface SidebarHistoryStyles {
  root: SxProps
  header: SxProps
  headerLabel: SxProps
  clear: SxProps
  clearLabel: SxProps
  list: SxProps
  empty: SxProps
  item: SxProps
  itemActive: SxProps
  itemButton: SxProps
  itemText: SxProps
  itemTextActive: SxProps
  remove: SxProps
  removeIcon: SxProps
}

// Titles sit one step inside the row so the hover and selected backgrounds read
// as inset rather than flush with the text.
const rowGutter = sidebarGutter

export const sidebarHistoryStyles: SidebarHistoryStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    px: sidebarGutter,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: rowGutter,
    pt: 1,
    pb: 1,
  },
  // Sentence case at the body size — uppercase micro-type read as cramped.
  headerLabel: {
    typography: 'textSmSemibold',
    color: 'text.secondary700',
  },
  clear: {
    p: 0.5,
    mr: -0.5,
    borderRadius: 1.5,
    color: 'text.tertiary600',
    '&:hover': { color: 'text.primary900' },
  },
  clearLabel: {
    typography: 'textSmMedium',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25,
    pb: 1,
  },
  empty: {
    px: rowGutter,
    py: 1,
    typography: 'textSm',
    color: 'text.placeHolder',
  },
  item: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: scaledPx(40),
    borderRadius: 2,
    color: 'text.secondary700',
    transition: 'background .15s, color .15s',
    '&:hover': {
      backgroundColor: 'utils.gray50',
      color: 'text.primary900',
    },
    // Reveal the remove action on hover, and whenever focus is inside the row
    // so it stays reachable by keyboard.
    '&:hover .history-remove, &:focus-within .history-remove': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    // Touch devices never hover, so keep it permanently visible there.
    '@media (hover: none)': {
      '& .history-remove': { opacity: 1, pointerEvents: 'auto' },
    },
  },
  // Three states that can't be confused: rest is transparent, hover is the grey
  // above, and the open conversation is brand-tinted with a bar on its edge. The
  // tint has to differ from hover — sharing it made the selection invisible
  // whenever the pointer was elsewhere.
  itemActive: {
    backgroundColor: 'background.primaryAlt',
    color: 'text.primary900',
    '&:hover': {
      backgroundColor: 'background.primaryAlt',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: scaledPx(3),
      height: '60%',
      borderRadius: 999,
      backgroundColor: 'brand.primary700',
    },
    // The open conversation keeps its remove action to hand without a hover.
    '& .history-remove': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  // The button *is* the row, padding included, so a click anywhere in it opens
  // the conversation. The remove action floats over its right edge rather than
  // nesting inside it, which no button may do.
  itemButton: {
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    textAlign: 'left',
    pl: rowGutter,
    // Room for the remove action, so a long title never runs under it.
    pr: 4.5,
    py: 1,
    borderRadius: 2,
    color: 'inherit',
  },
  itemText: {
    width: '100%',
    typography: 'textSmMedium',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemTextActive: {
    typography: 'textSmSemibold',
  },
  remove: {
    position: 'absolute',
    top: '50%',
    // Matches the row gutter; `right` is not spacing-aware, hence the px value.
    right: scaledPx(8),
    transform: 'translateY(-50%)',
    opacity: 0,
    pointerEvents: 'none',
    flexShrink: 0,
    width: scaledPx(24),
    height: scaledPx(24),
    p: 0,
    borderRadius: 1.5,
    color: 'text.tertiary600',
    transition: 'opacity .15s, background .15s, color .15s',
    '&:hover': {
      backgroundColor: 'utils.gray200',
      color: 'text.primary900',
    },
    '&:focus-visible': { opacity: 1, pointerEvents: 'auto' },
  },
  removeIcon: {
    width: scaledPx(14),
    height: scaledPx(14),
  },
}
